import {
	createContext,
	type MutableRefObject,
	type PropsWithChildren,
	type ReactNode,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';

import { type AnalyticsBase } from '../server/AnalyticsBase';
import { AnalyticsIframe } from '../server/components/AnalyticsIframe';
import { type AppEventContext } from '../server/EventRouterService';
import { type Environment } from '../server/utils/environments';

export type AnalyticsUserInfo = {
	id: number;
	userData: Record<string, unknown>;
};

type Props = {
	handler: AnalyticsBase;
	pageName: string;
	appEventContext?: AppEventContext;
	userInfo?: AnalyticsUserInfo;
	env: Environment;
};

type AnalyticsContextType = {
	skipAutomaticPageTracking: MutableRefObject<boolean>;
};

const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

export function AnalyticsProvider({
	handler,
	userInfo,
	env,
	appEventContext,
	pageName,
	children,
}: PropsWithChildren<Props>) {
	const [segmentIframeEnabled, setSegmentIframeEnabled] = useState(false);
	const skipAutomaticPageTracking = useRef(false);
	const segmentIframeRef = useRef<HTMLIFrameElement>(null);

	useEffect(() => {
		if (Object.keys(handler.requiredIframes).length === 0) {
			return;
		}

		// Manually enable the iframe to control render order.
		if (!segmentIframeEnabled) {
			setSegmentIframeEnabled(true);
			return;
		}

		if (!segmentIframeRef.current) {
			return;
		}

		const segmentIframeCurrent = segmentIframeRef.current;

		const handleIframeLoadEvent = () => {
			handler.init(appEventContext, segmentIframeCurrent);
		};

		segmentIframeCurrent?.addEventListener('load', handleIframeLoadEvent);

		return () => {
			segmentIframeCurrent?.removeEventListener('load', handleIframeLoadEvent);
		};
	}, [appEventContext, handler, segmentIframeEnabled]);

	useEffect(() => {
		if (Object.keys(handler.requiredIframes).length > 0) {
			return;
		}

		// If the document is already loaded, we can initialize the analytics handler.
		if (document.readyState === 'complete') {
			handler.init(appEventContext);
			return;
		}

		const initializeHandler = () => {
			handler.init(appEventContext);
		};

		// If the document is not loaded yet, we need to wait for it to be fully loaded before initializing the analytics handler. We can't rely only on this event listener because document might be already loaded when this component is mounted so it won't trigger the `readystatechange` event. That's why we need to check the document.readyState in the beginning of this useEffect.
		window.document.addEventListener('readystatechange', initializeHandler);

		return () => {
			window.document.removeEventListener('readystatechange', initializeHandler);
		};
	}, [appEventContext, handler]);

	useEffect(() => {
		if (userInfo) {
			handler.identify(userInfo.id, userInfo.userData);
		}
	}, [handler, userInfo]);

	useEffect(() => {
		const isPageNameEmpty = pageName === '';
		if (skipAutomaticPageTracking.current || isPageNameEmpty) {
			return;
		}

		handler.trackPage(pageName);
	}, [handler, pageName]);

	return (
		<AnalyticsContext.Provider value={{ skipAutomaticPageTracking }}>
			{children}
			{segmentIframeEnabled
				? (createPortal(<AnalyticsIframe env={env} ref={segmentIframeRef} />, document.body) as ReactNode)
				: null}
		</AnalyticsContext.Provider>
	);
}

export function useBlockAutomaticPageTracking() {
	const context = useContext(AnalyticsContext);

	if (!context) {
		throw new Error('useBlockAutomaticPageTracking must be used within the AnalyticsProvider');
	}

	const { skipAutomaticPageTracking } = context;
	skipAutomaticPageTracking.current = true;

	useEffect(() => {
		return () => {
			skipAutomaticPageTracking.current = false;
		};
	}, [skipAutomaticPageTracking]);

	return null;
}
